import { Logo } from "@koaform/ui";
import {
  CheckIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  LinkIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthForm } from "../auth/AuthForm.js";
import { usePageTitle } from "../hooks/usePageTitle.js";
import { api, type Draft, type Me } from "../lib/api.js";
import { ShareDialog } from "../share/ShareDialog.js";
import { AccountDialog } from "./AccountDialog.js";
import { HelpDialog } from "./HelpDialog.js";

const RENDERER_URL =
  import.meta.env.VITE_RENDERER_URL ?? "http://localhost:5052";
const MARKETING_URL =
  import.meta.env.VITE_MARKETING_URL ??
  (import.meta.env.PROD ? "https://koaform.com" : "http://localhost:5053");

/**
 * The app's front door.
 *
 * Signed in → your forms. Signed out → log in, with an explicit "just start
 * building" path so nobody is forced through an account to try the product.
 * Until this existed, "Log in" on the marketing site dropped people into an
 * empty builder with no way to reach their own forms.
 */
export const Home = () => {
  const [state, setState] = useState<
    { phase: "loading" } | { phase: "anonymous" } | { phase: "in"; me: Me }
  >({ phase: "loading" });

  const load = async () => {
    try {
      const me = await api.me();
      setState({ phase: "in", me });
    } catch {
      setState({ phase: "anonymous" });
    }
  };

  useEffect(() => {
    void load();
  }, []);

  if (state.phase === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-paper">
        <p className="text-sm text-faint">Loading…</p>
      </div>
    );
  }

  if (state.phase === "anonymous") return <Login onSignedIn={load} />;
  return <Dashboard me={state.me} onSignedOut={load} />;
};

const Login = ({ onSignedIn }: { onSignedIn: () => Promise<void> }) => {
  usePageTitle("Log in");
  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <header className="px-8 py-5">
        <a href={MARKETING_URL} aria-label="koaform">
          <Logo size={30} />
        </a>
      </header>

      <main className="flex flex-1 items-start justify-center px-6 pt-[8vh] pb-16">
        <div className="w-[420px] max-w-full">
          <div className="animate-rise rounded-[20px] border border-line-strong bg-surface p-8 shadow-[0_18px_50px_-34px_rgba(12,26,46,0.4)]">
            <h1 className="m-0 mb-2 text-[25px] leading-[1.15] font-extrabold tracking-[-0.03em]">
              Welcome back
            </h1>
            <p className="m-0 mb-6 text-[15px] leading-[1.55] text-muted">
              Log in to see your forms and responses.
            </p>
            <AuthForm onSignedIn={onSignedIn} submitLabel="Log in" />
          </div>

          <p className="mt-5 text-center text-[14px] text-muted">
            New here?{" "}
            <a href="/new" className="font-semibold">
              Start building — no account needed
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

const STATUS: Record<Draft["status"], { label: string; cls: string }> = {
  published: { label: "Live", cls: "bg-primary-100 text-primary-600" },
  draft: { label: "Draft", cls: "bg-line text-body" },
  unpublished: { label: "Paused", cls: "bg-amber-100 text-amber-700" },
};

const when = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
};

const Dashboard = ({
  me,
  onSignedOut,
}: {
  me: Me;
  onSignedOut: () => Promise<void>;
}) => {
  const [forms, setForms] = useState<Draft[] | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [shareFor, setShareFor] = useState<Draft | null>(null);
  const [renaming, setRenaming] = useState<Draft | null>(null);
  const [account, setAccount] = useState<Me | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  usePageTitle("Your forms");

  const reload = () => api.listDrafts().then(setForms);

  useEffect(() => {
    void reload();
  }, []);

  // One open menu at a time, and any outside click closes it.
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuFor]);

  const copyLink = async (form: Draft) => {
    if (!form.slug) return;
    try {
      await navigator.clipboard.writeText(`${RENDERER_URL}/r/${form.slug}`);
      setCopiedId(form.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      // Clipboard needs a secure context; the Share dialog has a field.
    }
  };

  const duplicate = async (form: Draft) => {
    setBusy(true);
    try {
      await api.duplicateDraft(form.id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (form: Draft) => {
    const live = form.status === "published";
    const warning = live
      ? `Delete “${form.title}”?\n\nIt is live, so the link stops working for anyone who has it. Responses already collected are kept.`
      : `Delete “${form.title}”?`;
    if (!window.confirm(warning)) return;
    setBusy(true);
    try {
      await api.deleteDraft(form.id);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const cardBtn =
    "rounded-lg border border-line-strong bg-surface px-3 py-1.5 text-[13px] font-semibold text-body hover:border-primary-600 hover:text-primary-600";

  return (
    <div className="min-h-dvh bg-paper">
      <header className="border-b border-line-strong bg-surface">
        <div className="mx-auto flex max-w-[1120px] items-center justify-between gap-4 px-8 py-4">
          <Logo size={30} />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              title="Help"
              aria-label="Help"
              className="grid h-10 w-10 place-items-center rounded-[10px] text-muted hover:bg-line hover:text-ink"
            >
              <QuestionMarkCircleIcon className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => setAccount(me)}
              title="Account settings"
              aria-label="Account settings"
              className="grid h-10 w-10 place-items-center rounded-[10px] text-muted hover:bg-line hover:text-ink"
            >
              <Cog6ToothIcon className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => void api.logout().then(onSignedOut)}
              className="ml-1.5 text-sm font-semibold text-muted hover:text-ink"
            >
              Log out
            </button>
            <button
              type="button"
              onClick={() => navigate("/new")}
              className="ml-2 rounded-[10px] bg-primary-600 px-[18px] py-[11px] text-[15px] font-bold text-white hover:bg-primary-700"
            >
              New form
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1120px] px-8 py-10">
        <h1 className="m-0 mb-6 text-[31px] font-extrabold tracking-[-0.035em]">
          Your forms
        </h1>

        {forms === null ? (
          <p className="text-sm text-faint">Loading…</p>
        ) : forms.length === 0 ? (
          <div className="rounded-[20px] border-[1.5px] border-dashed border-line-bold bg-surface px-8 py-16 text-center">
            <p className="m-0 mb-2 text-[19px] font-bold tracking-[-0.02em]">
              No forms yet
            </p>
            <p className="mx-auto m-0 mb-7 max-w-[380px] text-[15px] leading-[1.55] text-muted">
              Build one from scratch, or start from a template that already asks
              the right questions.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/new")}
                className="rounded-[11px] bg-primary-600 px-5 py-3 text-[15px] font-bold text-white hover:bg-primary-700"
              >
                Start from scratch
              </button>
              <a
                href={`${MARKETING_URL}/templates`}
                className="rounded-[11px] border border-line-bold bg-surface px-5 py-3 text-[15px] font-semibold text-ink hover:border-primary-600 hover:text-ink"
              >
                Browse templates
              </a>
            </div>
          </div>
        ) : (
          <ul className="grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
            {forms.map((form) => {
              const status = STATUS[form.status];
              return (
                /*
                 * The whole card opens the editor — that is what people try
                 * first. Every control inside stops propagation so it does
                 * its own job instead.
                 */
                <li
                  key={form.id}
                  onClick={() => navigate(`/forms/${form.id}/edit`)}
                  className="group relative flex cursor-pointer flex-col rounded-[16px] border border-line-strong bg-surface p-5 hover:border-primary-600"
                >
                  <div className="mb-1.5 flex items-start justify-between gap-3">
                    <h2 className="m-0 min-w-0 truncate text-[16px] font-bold tracking-[-0.015em] text-ink group-hover:text-primary-600">
                      {form.name ?? form.title}
                    </h2>
                    <div className="flex flex-none items-center gap-1.5">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${status.cls}`}
                      >
                        {status.label}
                      </span>
                      <button
                        type="button"
                        aria-label="Form options"
                        aria-haspopup="menu"
                        aria-expanded={menuFor === form.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuFor(menuFor === form.id ? null : form.id);
                        }}
                        className="grid h-7 w-7 place-items-center rounded-lg text-faint hover:bg-line hover:text-ink"
                      >
                        <EllipsisHorizontalIcon className="size-5" />
                      </button>
                    </div>
                  </div>

                  <p className="m-0 mb-4 font-mono text-[11px] text-faint">
                    {form.responsesCount}{" "}
                    {form.responsesCount === 1 ? "response" : "responses"}
                    {" \u00b7 "}edited {when(form.updatedAt)}
                  </p>

                  <div
                    className="mt-auto flex flex-wrap items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/forms/${form.id}/edit`)}
                      className={cardBtn}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/forms/${form.id}/responses`)}
                      className={cardBtn}
                    >
                      Responses
                    </button>
                    {form.slug ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setShareFor(form)}
                          className={cardBtn}
                        >
                          Share
                        </button>
                        <button
                          type="button"
                          title="Copy the link"
                          aria-label="Copy the link"
                          onClick={() => void copyLink(form)}
                          className="grid h-[30px] w-[30px] place-items-center rounded-lg border border-line-strong bg-surface text-faint hover:border-primary-600 hover:text-primary-600"
                        >
                          {copiedId === form.id ? (
                            <CheckIcon className="size-4 text-primary-600" />
                          ) : (
                            <LinkIcon className="size-4" />
                          )}
                        </button>
                      </>
                    ) : null}
                  </div>

                  {menuFor === form.id ? (
                    <div
                      role="menu"
                      onClick={(e) => e.stopPropagation()}
                      className="animate-rise absolute top-12 right-4 z-10 w-[184px] overflow-hidden rounded-[12px] border border-line-strong bg-surface py-1 shadow-[0_18px_44px_-16px_rgba(12,26,46,0.35)]"
                    >
                      {[
                        { label: "Rename", run: () => setRenaming(form) },
                        { label: "Duplicate", run: () => void duplicate(form) },
                      ].map((item) => (
                        <button
                          key={item.label}
                          role="menuitem"
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setMenuFor(null);
                            item.run();
                          }}
                          className="block w-full px-4 py-2.5 text-left text-sm font-semibold text-body hover:bg-paper disabled:opacity-50"
                        >
                          {item.label}
                        </button>
                      ))}
                      <span className="my-1 block h-px bg-line" />
                      <button
                        role="menuitem"
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setMenuFor(null);
                          void remove(form);
                        }}
                        className="block w-full px-4 py-2.5 text-left text-sm font-semibold text-danger hover:bg-danger/5 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {shareFor?.slug ? (
        <ShareDialog
          slug={shareFor.slug}
          title={shareFor.title}
          onClose={() => setShareFor(null)}
        />
      ) : null}

      {renaming ? (
        <RenameDialog
          form={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={() => {
            setRenaming(null);
            void reload();
          }}
        />
      ) : null}

      {helpOpen ? <HelpDialog onClose={() => setHelpOpen(false)} /> : null}

      {account ? (
        <AccountDialog
          me={account}
          onSaved={() => void 0}
          onClose={() => setAccount(null)}
        />
      ) : null}
    </div>
  );
};

/** Rename without opening the editor — the title lives in the document. */
const RenameDialog = ({
  form,
  onClose,
  onRenamed,
}: {
  form: Draft;
  onClose: () => void;
  onRenamed: () => void;
}) => {
  const [title, setTitle] = useState(form.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Empty is allowed: it clears the private name and the card falls
      // back to showing the form's public title.
      await api.renameDraft(form.id, title.trim());
      onRenamed();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not rename that form.",
      );
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Rename form"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="animate-fade fixed inset-0 z-50 flex items-start justify-center bg-ink/45 p-6 backdrop-blur-[3px]"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="animate-rise mt-[12vh] w-[420px] max-w-full rounded-[20px] bg-surface p-7 shadow-[0_30px_70px_-20px_rgba(12,26,46,0.5)]"
      >
        <h2 className="m-0 mb-1.5 text-[21px] font-extrabold tracking-[-0.025em]">
          Rename form
        </h2>
        <p className="m-0 mb-4 text-[13.5px] leading-[1.5] text-muted">
          Just for your dashboard — respondents still see “{form.title}”.
        </p>
        <input
          autoFocus
          value={title}
          maxLength={200}
          placeholder={form.title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          className="mb-1 block w-full rounded-[11px] border border-line-input p-[13px] text-[15px] text-ink focus:border-primary-600 focus:outline-none"
        />
        {error ? (
          <p role="alert" className="mt-2 mb-0 text-[13.5px] text-danger">
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-line-bold bg-surface px-4 py-2.5 text-sm font-semibold text-ink hover:border-primary-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-[10px] bg-primary-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-700 disabled:bg-line-bold"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};
